import { useRef, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useConfirmDialog } from '@/components/common/confirm-dialog'
import { OrderItemMediaPreview } from '@/components/orders/order-item-media-preview'
import { Button } from '@/components/ui/button'
import { getErrorMessage } from '@/lib/api'
import { ordersApi } from '@/services/orders'
import type { StoreOrderItemMedia } from '@/types'

interface OrderItemMediaProps {
  orderItemId?: string | number | null
  basketItemId?: string | number | null
  serviceInstanceId?: string | number | null
  canEdit: boolean
}

export function OrderItemMedia({ orderItemId, basketItemId, serviceInstanceId, canEdit }: OrderItemMediaProps) {
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasOrderContext = Boolean(orderItemId && String(orderItemId).trim())
  const hasBasketContext = Boolean(basketItemId && String(basketItemId).trim())
  const mediaQueryKey = [
    'order-item-media',
    hasOrderContext ? `order:${String(orderItemId)}` : hasBasketContext ? `basket:${String(basketItemId)}` : 'none',
    serviceInstanceId ? String(serviceInstanceId) : 'default',
  ]

  const mediaQuery = useQuery({
    queryKey: mediaQueryKey,
    queryFn: () => ordersApi.listOrderItemMedia({
      orderItemId: hasOrderContext ? orderItemId : null,
      basketItemId: hasOrderContext ? null : basketItemId,
      serviceInstanceId,
    }),
    enabled: hasOrderContext || hasBasketContext,
  })

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => ordersApi.createOrderItemMedia({
      orderItemId: hasOrderContext ? orderItemId : null,
      basketItemId: hasOrderContext ? null : basketItemId,
      serviceInstanceId,
    }, files),
    onSuccess: async () => {
      toast.success('Media uploaded')
      await queryClient.invalidateQueries({ queryKey: mediaQueryKey })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (mediaId: string | number) => ordersApi.deleteOrderItemMedia(mediaId),
    onSuccess: async () => {
      toast.success('Media deleted')
      await queryClient.invalidateQueries({ queryKey: mediaQueryKey })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length === 0 || (!hasOrderContext && !hasBasketContext)) {
      return
    }

    uploadMutation.mutate(files)
    event.target.value = ''
  }

  const handleDelete = (media: StoreOrderItemMedia) => {
    confirm({
      title: 'Delete media file',
      description: 'Delete this media file? This action cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: () => deleteMutation.mutate(media.id),
    })
  }

  const mediaList = mediaQuery.data ?? []

  return (
    <div className="mt-3 space-y-2">
      <ConfirmDialog />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-sky-700">Media</span>
        {canEdit && (hasOrderContext || hasBasketContext) ? (
          <>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileChange}
              accept="image/*,.pdf,.doc,.docx"
              multiple
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </>
        ) : null}
      </div>

      {!hasOrderContext && !hasBasketContext ? (
        <p className="text-xs text-muted-foreground">Media unavailable for this line item.</p>
      ) : mediaQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading media...</p>
      ) : mediaQuery.isError ? (
        <p className="text-xs text-destructive">{getErrorMessage(mediaQuery.error)}</p>
      ) : mediaList.length === 0 ? (
        <p className="text-xs text-muted-foreground">No media uploaded.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {mediaList.map((media) => (
            <OrderItemMediaPreview
              key={media.id}
              media={media}
              onRemove={canEdit ? () => handleDelete(media) : undefined}
              removeDisabled={deleteMutation.isPending}
              showLabel
            />
          ))}
        </div>
      )}
    </div>
  )
}
